import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import FormSection from '../components/FormSection';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { Org, Venue } from '../api/types';

const SAMPLE_ROSTER = `Sam Speedy, 10, Boys, parent1@example.com
Maya Long, 9, Girls, parent2@example.com
Jordan Relay, 11, Boys, parent3@example.com`;

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [active, setActive] = useState(0);
  const [rosterText, setRosterText] = useState(SAMPLE_ROSTER);
  const [coachEmail, setCoachEmail] = useState('');
  const [meetName, setMeetName] = useState('First Meet');
  const [meetDate, setMeetDate] = useState(dayjs().add(7, 'day').format('YYYY-MM-DD'));
  const [venueName, setVenueName] = useState('');
  const [checkIn, setCheckIn] = useState('Arrive 60 minutes before your first event.');

  const orgQuery = useQuery<Org>({
    queryKey: ['org', 'me'],
    queryFn: async () => (await api.get('/orgs/me')).data,
  });
  const venuesQuery = useQuery<Venue[]>({
    queryKey: ['venues'],
    queryFn: async () => (await api.get('/venues')).data,
  });

  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('school');
  const [timezone, setTimezone] = useState('America/New_York');

  useEffect(() => {
    if (orgQuery.data && !orgName) {
      setOrgName(orgQuery.data.name);
      setOrgType(orgQuery.data.type || 'school');
      setTimezone(orgQuery.data.timezone || 'America/New_York');
    }
  }, [orgQuery.data, orgName]);

  const saveOrg = useMutation({
    mutationFn: async () => (await api.patch('/orgs/me', { name: orgName, type: orgType, timezone })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', 'me'] });
      setActive(1);
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const importRoster = useMutation({
    mutationFn: async () => (await api.post('/students/import', { text: rosterText })).data,
    onSuccess: (data) => {
      toast({
        message: `Imported ${data.created_count} athletes${data.skipped_count ? `, skipped ${data.skipped_count}` : ''}.`,
        severity: 'success',
      });
      qc.invalidateQueries({ queryKey: ['students'] });
      setActive(2);
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const inviteCoach = useMutation({
    mutationFn: async () => api.post('/invites', { email: coachEmail, role: 'coach' }),
    onSuccess: () => {
      toast({ message: 'Coach invite sent', severity: 'success' });
      setCoachEmail('');
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const createMeet = useMutation({
    mutationFn: async () => {
      let venueId = venuesQuery.data?.[0]?.id;
      if (venueName.trim()) {
        const venue = (await api.post('/venues', { name: venueName.trim() })).data;
        venueId = venue.id;
      }
      const meet = (await api.post('/meets', {
        name: meetName,
        start_date: meetDate,
        end_date: meetDate,
        venue_id: venueId,
        check_in_instructions: checkIn,
      })).data;
      return meet;
    },
    onSuccess: (meet) => {
      toast({ message: 'First meet created. Time to prepare it.', severity: 'success' });
      qc.invalidateQueries({ queryKey: ['meets'] });
      navigate(`/meets/${meet.id}/prepare`);
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  return (
    <Box>
      <PageHeader title="Set up Baton" subtitle="Get from empty workspace to first meet in a few minutes." />
      <Card>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Stepper activeStep={active} alternativeLabel sx={{ mb: 4 }}>
            {['Team', 'Roster', 'Coaches', 'First meet'].map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
          {(saveOrg.isPending || importRoster.isPending || createMeet.isPending) && <LinearProgress sx={{ mb: 2 }} />}

          {active === 0 && (
            <FormSection title="Team basics" description="This appears on parent emails and meet packets.">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Team / org name" value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField label="Type" select value={orgType} onChange={(e) => setOrgType(e.target.value)}>
                    <MenuItem value="school">School</MenuItem>
                    <MenuItem value="club">Club</MenuItem>
                    <MenuItem value="district">District</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
                </Grid>
              </Grid>
              <Button variant="contained" sx={{ mt: 3 }} disabled={!orgName || saveOrg.isPending} onClick={() => saveOrg.mutate()}>
                Save team
              </Button>
            </FormSection>
          )}

          {active === 1 && (
            <FormSection title="Paste your roster" description="One athlete per line: Name, grade, gender, parent email. Parents do not need accounts yet.">
              <TextField
                label="Roster"
                multiline
                minRows={10}
                value={rosterText}
                onChange={(e) => setRosterText(e.target.value)}
                helperText="Example: Sam Speedy, 10, Boys, parent@example.com"
              />
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button onClick={() => setActive(0)}>Back</Button>
                <Button variant="contained" disabled={!rosterText.trim() || importRoster.isPending} onClick={() => importRoster.mutate()}>
                  Import athletes
                </Button>
              </Stack>
            </FormSection>
          )}

          {active === 2 && (
            <FormSection title="Invite assistant coaches" description="Optional. You can skip this and invite people later.">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField label="Coach email" value={coachEmail} onChange={(e) => setCoachEmail(e.target.value)} type="email" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button fullWidth variant="outlined" sx={{ height: 40 }} disabled={!coachEmail || inviteCoach.isPending} onClick={() => inviteCoach.mutate()}>
                    Send invite
                  </Button>
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button onClick={() => setActive(1)}>Back</Button>
                <Button variant="contained" onClick={() => setActive(3)}>Continue</Button>
              </Stack>
            </FormSection>
          )}

          {active === 3 && (
            <FormSection title="Create your first meet" description="You can add events and entries on the next screen.">
              <Alert severity="info" sx={{ mb: 2 }}>
                The first meet is where Baton starts to feel useful: entries, parent emails, coach pack, and meet-day mode.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><TextField label="Meet name" value={meetName} onChange={(e) => setMeetName(e.target.value)} required /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Meet date" type="date" value={meetDate} onChange={(e) => setMeetDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12}><TextField label="Venue name" value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder={venuesQuery.data?.[0]?.name || 'Home track'} /></Grid>
                <Grid item xs={12}><TextField label="Check-in instructions" multiline minRows={2} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} /></Grid>
              </Grid>
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button onClick={() => setActive(2)}>Back</Button>
                <Button variant="contained" disabled={!meetName || !meetDate || createMeet.isPending} onClick={() => createMeet.mutate()}>
                  Create meet and prepare
                </Button>
              </Stack>
            </FormSection>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
